import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { LoyaltyCard } from '@src/components/CustomerLoyaltyCard/CustomerLoyaltyCard'
import { Helmet } from '@src/components/Helmet'
import { allOutletDiscountsQuery } from '@src/hooks/sharedQueries/useAllDiscounts/queries/__generated__/getAllDiscounts.graphql-interface'
import { useBasketOutletId } from '@src/hooks/useBasketOutletId'
import { useBasketTotals } from '@src/hooks/useTotals/useBasketTotals'

import { MenuGroup } from './MenuGroup/MenuGroup'
import { MenuItemSearchResults } from './MenuItemSearchResults/MenuItemSearchResults'
import {
  DealsAndOffersContainer,
  SingleDiscountContainer,
} from './OutletMenuStyles'

import { SingleDiscount } from '../OutletDiscounts/SingleDiscount'
import { ScrollToContainer } from '../OutletMenu/MenuGroup/MenuGroup.styles'
import { DealsAndOffersSkeleton } from '../OutletPageSkeleton/DealsAndOffersSkeleton'
import { useOutletPageContext } from '../OutletPageContext'
import { CustomerDetailsAndAddressesQuery } from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/queries/__generated__/CustomerDetailsAndAddressesQuery.graphql-interface'
import { getAddOnItemsForOutletQuery } from '../queries/__generated__/getAddOnItemsForOutlet.graphql-interface'
import { AddOnItemsOffer } from '../OutletDiscounts/AddOnItemsOffer'
import { first } from 'lodash'

// Used for the active menu section capsule highlight and navigation
const MenuSection: React.FC<{
  menuId: string
  children: React.ReactNode
  setActiveMenuId: (id: string) => void
  manualNavigation: boolean
  showMenuSearchResults: boolean
  isListening: boolean
  rootMargin: string
}> = ({
  menuId,
  children,
  setActiveMenuId,
  manualNavigation,
  showMenuSearchResults,
  isListening,
  rootMargin,
}) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin,
  })

  useEffect(() => {
    if (inView && !manualNavigation && isListening && !showMenuSearchResults)
      setActiveMenuId(menuId)
  }, [
    inView,
    menuId,
    setActiveMenuId,
    manualNavigation,
    isListening,
    showMenuSearchResults,
  ])

  return (
    <div ref={ref} id={menuId}>
      {children}
    </div>
  )
}

export const OutletMenu: React.FC<{
  allDiscounts: allOutletDiscountsQuery | undefined
  customerDetails: CustomerDetailsAndAddressesQuery | undefined
  addOnItems: getAddOnItemsForOutletQuery | undefined
  addOnItemsLoading: boolean
  customerDetailsLoading: boolean
  discountsLoading: boolean
}> = ({
  allDiscounts,
  discountsLoading,
  customerDetails,
  customerDetailsLoading,
  addOnItems,
  addOnItemsLoading,
}) => {
  const {
    outletFulfilment: { outlet },
    setActiveMenuId,
    treeForModalData,
    manualNavigation,
    showMenuSearchResults,
    setManualNavigation,
    outletFulfilment,
  } = useOutletPageContext()

  const allowAddOnItems =
    outletFulfilment?.outlet?.restaurant?.allowAddOnItems ?? false
  const { data: basketTotals } = useBasketTotals()
  const { basketOutletId } = useBasketOutletId()
  const scrollStartY = useRef<number>(0)
  const [isListening, setIsListening] = useState<boolean>(false)

  const handleScroll = useCallback(() => {
    if (!manualNavigation || !isListening) return

    const currentScrollY = window.scrollY
    const scrollDelta = Math.abs(currentScrollY - scrollStartY.current)

    if (scrollDelta > 50) {
      setManualNavigation(false)
      setIsListening(false)
    }
  }, [manualNavigation, setManualNavigation])

  useEffect(() => {
    if (manualNavigation) {
      scrollStartY.current = window.scrollY
      setIsListening(false)
    }

    const manualScrollTimeout = setTimeout(() => {
      setIsListening(true)
    }, 800)

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      clearTimeout(manualScrollTimeout)
      setIsListening(false)
    }
  }, [manualNavigation, handleScroll])

  // All the whacky logic above is to strike a balance between manual and scroll navigation observer ranges
  const rootMargin = useMemo(
    () =>
      manualNavigation
        ? '-100px 0px -80% 0px' // Tight detection during manual nav - prevents "overshooting" small and narrow sections when setting active menu
        : '-100px 0px -60% 0px',
    [manualNavigation]
  )

  const thisOutletIsInBasket = basketOutletId === outlet.id

  const loyaltyCards = (() => {
    if (
      thisOutletIsInBasket &&
      basketTotals?.checkoutBasketTotals?.loyaltyCards
    ) {
      return basketTotals.checkoutBasketTotals.loyaltyCards.length
        ? basketTotals.checkoutBasketTotals.loyaltyCards
        : outlet.outletLoyaltyCards
    }

    const cards =
      customerDetails?.customerDetails.loyaltyCards ?? outlet.outletLoyaltyCards

    return cards
  })()

  const hasDealsSection =
    (allDiscounts?.allDiscountsByOutletId?.length ||
      loyaltyCards?.length ||
      addOnItems?.addOnMenuItems?.length) &&
    !showMenuSearchResults

  const renderedMenuGroups = useMemo(
    () =>
      treeForModalData.map(outletMenuGroup =>
        outletMenuGroup.parentMenu ? (
          <MenuSection
            key={outletMenuGroup.parentMenu.id}
            menuId={outletMenuGroup.parentMenu.id}
            setActiveMenuId={setActiveMenuId}
            manualNavigation={manualNavigation}
            showMenuSearchResults={showMenuSearchResults}
            isListening={isListening}
            rootMargin={rootMargin}
          >
            <MenuGroup
              outletId={outlet.id}
              outletParentMenu={outletMenuGroup.parentMenu}
              outletSubMenus={outletMenuGroup.subMenus}
            />
          </MenuSection>
        ) : null
      ),
    [
      treeForModalData,
      outlet.id,
      setActiveMenuId,
      manualNavigation,
      showMenuSearchResults,
      isListening,
      rootMargin,
    ]
  )

  return (
    <>
      <Helmet title={`${outlet.displayName} Menu`} />
      {discountsLoading || customerDetailsLoading || addOnItemsLoading ? (
        <DealsAndOffersSkeleton />
      ) : hasDealsSection ? (
        <MenuSection
          menuId="discountMenuItem"
          setActiveMenuId={setActiveMenuId}
          manualNavigation={manualNavigation}
          showMenuSearchResults={showMenuSearchResults}
          isListening={isListening}
          rootMargin={rootMargin}
        >
          <ScrollToContainer id="discountMenuItem" aria-hidden />
          <DealsAndOffersContainer>
            <SingleDiscountContainer>
              {loyaltyCards.map(loyaltyCard => (
                <LoyaltyCard
                  loyaltyCard={loyaltyCard}
                  key={loyaltyCard.id}
                  basketTotals={
                    thisOutletIsInBasket && basketTotals
                      ? basketTotals
                      : undefined
                  }
                />
              ))}
              {allDiscounts?.allDiscountsByOutletId.map(discount => (
                <div key={discount.id}>
                  <SingleDiscount allDiscounts={discount} />
                </div>
              ))}
              {allowAddOnItems && addOnItems?.addOnMenuItems?.length ? (
                <AddOnItemsOffer
                  parentMenuName={
                    first(addOnItems.addOnMenuItems)?.parentMenu.name
                  }
                  minimumBasketTotalForAddOns={
                    outlet.restaurant.minimumBasketTotalForAddOns
                  }
                />
              ) : null}
            </SingleDiscountContainer>
          </DealsAndOffersContainer>
        </MenuSection>
      ) : null}
      {showMenuSearchResults ? (
        <MenuItemSearchResults outletId={outlet.id} />
      ) : (
        renderedMenuGroups
      )}
    </>
  )
}
